import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ListingDetail, ListingSummary, SearchOptions } from '@/types'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useState } from 'react'
import {
    BedDouble,
    CalendarDays,
    ChevronLeft,
    MapPin,
    Palette,
    Phone,
    Ruler,
    ShowerHead
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { getCatalog, getListingDetail, getSearchOptions } from '@/lib/catalog'
import { similarHomes } from '@/lib/match'
import { homesHref } from '@/lib/search'
import { getIcon } from '@/lib/icons'

import {
    formatBaths,
    formatDate,
    formatLot,
    formatNumber,
    formatPrice,
    plural
} from '@/lib/format'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { SaveButton } from '@/components/homes/save-button'
import { ListingRow } from '@/components/homes/listing-row'
import { PhotoMosaic } from '@/components/listing/photo-mosaic'
import { PhotoTourDialog } from '@/components/listing/photo-tour-dialog'
import { TourRequestCard } from '@/components/listing/tour-request-card'
import { MortgageEstimate } from '@/components/listing/mortgage-estimate'
import { ShareButton } from '@/components/listing/share-button'
import { AgentAvatar } from '@/components/agents/agent-avatar'

const LocationMap = dynamic(() => import('@/components/listing/location-map'), {
    ssr: false,
    loading: () => <div className='h-full w-full animate-pulse bg-muted' />
})

interface ListingPageProps {
    listing: ListingDetail
    similar: ListingSummary[]
    searchOptions: SearchOptions
}

const ListingPage: FC<ListingPageProps> = ({ listing, similar, searchOptions }): ReactNode => {
    const [tourOpen, setTourOpen] = useState(false)
    const [tourStart, setTourStart] = useState<number | null>(null)
    const [expanded, setExpanded] = useState(false)

    const openTour = (index: number) => {
        setTourStart(index >= 0 ? index : null)
        setTourOpen(true)
    }

    const longDescription = (listing.description?.length ?? 0) > 420

    return (
        <Layout searchOptions={searchOptions} showTabBar={false} bottomBar>
            <Seo
                title={listing.title}
                description={listing.summary ?? `A ${listing.property_type.toLowerCase()} for sale in ${listing.city ?? 'California'}.`}
                ogImage={listing.cover ?? undefined}
            />

            <article className='mx-auto max-w-[1280px] px-4 pt-4 pb-10 sm:px-6 md:pt-6 lg:px-10 lg:pb-16'>
                <Link href='/homes' className='mb-4 inline-flex items-center gap-1 rounded-lg py-1 pr-2 text-sm font-semibold hover:underline md:hidden'>
                    <ChevronLeft className='size-4' aria-hidden='true' />
                    All homes
                </Link>

                <header className='mb-5 flex flex-wrap items-end justify-between gap-3'>
                    <div className='min-w-0'>
                        <h1 className='font-heading text-2xl font-bold tracking-tight sm:text-3xl'>
                            <span data-mdlfy-cms={`listings:${listing.id}:title`}>{listing.title}</span>
                        </h1>
                        <p className='mt-1 flex items-center gap-1.5 text-sm text-muted-foreground'>
                            <MapPin className='size-4 shrink-0' aria-hidden='true' />
                            <span>
                                {listing.address && <><span data-mdlfy-cms={`listings:${listing.id}:address`}>{listing.address}</span>, </>}
                                {listing.neighborhood && <span data-mdlfy-cms={`listings:${listing.id}:neighborhood`}>{listing.neighborhood}</span>}
                                {listing.neighborhood && listing.city && ', '}
                                {listing.city && <span data-mdlfy-cms={`listings:${listing.id}:city`}>{listing.city}</span>}
                                {listing.neighborhood || listing.city ? ', California' : 'California'}
                            </span>
                        </p>
                    </div>

                    <div className='-mr-3 flex items-center'>
                        <ShareButton title={listing.title} />
                        <SaveButton listingId={listing.id} title={listing.title} variant='inline' />
                    </div>
                </header>

                <PhotoMosaic listing={listing} onOpen={openTour} />

                <div className='mt-8 gap-16 lg:mt-12 lg:grid lg:grid-cols-[minmax(0,1fr)_23rem]'>
                    <div className='min-w-0'>
                        <section id='overview' aria-labelledby='overview-title' className='border-b border-border pb-8'>
                            <h2 id='overview-title' className='text-xl font-semibold sm:text-2xl'>
                                <span data-mdlfy-cms={`listings:${listing.id}:property_type`}>{listing.property_type}</span>
                                {listing.neighborhood && <> in <span data-mdlfy-cms={`listings:${listing.id}:neighborhood`}>{listing.neighborhood}</span></>}
                            </h2>

                            <ul className='mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4'>
                                <li className='rounded-xl border border-border p-4'>
                                    <BedDouble className='size-5 text-muted-foreground' aria-hidden='true' />
                                    <p className='mt-2 text-lg font-semibold'>{listing.bedrooms}</p>
                                    <p className='text-sm text-muted-foreground'>{plural(listing.bedrooms, 'bedroom')}</p>
                                </li>
                                <li className='rounded-xl border border-border p-4'>
                                    <ShowerHead className='size-5 text-muted-foreground' aria-hidden='true' />
                                    <p className='mt-2 text-lg font-semibold'>{formatBaths(listing.bathrooms)}</p>
                                    <p className='text-sm text-muted-foreground'>{plural(listing.bathrooms, 'bathroom')}</p>
                                </li>
                                {listing.area_sqft !== null && (
                                    <li className='rounded-xl border border-border p-4'>
                                        <Ruler className='size-5 text-muted-foreground' aria-hidden='true' />
                                        <p className='mt-2 text-lg font-semibold'>{formatNumber(listing.area_sqft)}</p>
                                        <p className='text-sm text-muted-foreground'>square feet</p>
                                    </li>
                                )}
                                {listing.year_built !== null && (
                                    <li className='rounded-xl border border-border p-4'>
                                        <CalendarDays className='size-5 text-muted-foreground' aria-hidden='true' />
                                        <p className='mt-2 text-lg font-semibold'>{listing.year_built}</p>
                                        <p className='text-sm text-muted-foreground'>year built</p>
                                    </li>
                                )}
                            </ul>

                            {(listing.lot_sqft !== null || listing.listed_at) && (
                                <p className='mt-4 text-sm text-muted-foreground'>
                                    {listing.lot_sqft !== null && <span>{formatLot(listing.lot_sqft)}</span>}
                                    {listing.lot_sqft !== null && listing.listed_at && ' · '}
                                    {listing.listed_at && <>Listed <span>{formatDate(listing.listed_at)}</span></>}
                                </p>
                            )}
                        </section>

                        <section id='highlights' aria-label='Highlights' className='space-y-6 border-b border-border py-8'>
                            {listing.style && (
                                <div className='flex gap-5'>
                                    <Palette className='mt-0.5 size-6 shrink-0' strokeWidth={1.5} aria-hidden='true' />
                                    <div>
                                        <p className='font-semibold'>
                                            <span data-mdlfy-cms={`styles:${listing.style.id}:name`}>{listing.style.name}</span> style
                                        </p>
                                        {listing.style.description && (
                                            <p className='text-sm text-muted-foreground'>
                                                <span data-mdlfy-cms={`styles:${listing.style.id}:description`}>{listing.style.description}</span>
                                            </p>
                                        )}
                                        <Link href={homesHref({ styles: [listing.style.slug] })} className='mt-1 inline-block text-sm font-semibold underline underline-offset-4'>
                                            See more homes in this style
                                        </Link>
                                    </div>
                                </div>
                            )}

                            {listing.area && (
                                <div className='flex gap-5'>
                                    <MapPin className='mt-0.5 size-6 shrink-0' strokeWidth={1.5} aria-hidden='true' />
                                    <div>
                                        <p className='font-semibold'>
                                            <span data-mdlfy-cms={`areas:${listing.area.id}:name`}>{listing.area.name}</span>
                                        </p>
                                        {listing.area.tagline && (
                                            <p className='text-sm text-muted-foreground'>
                                                <span data-mdlfy-cms={`areas:${listing.area.id}:tagline`}>{listing.area.tagline}</span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {listing.agent && (
                                <div className='flex items-center gap-5'>
                                    <AgentAvatar agent={listing.agent} className='size-10' sizes='40px' />
                                    <p className='text-sm'>
                                        Listed by <span className='font-semibold' data-mdlfy-cms={`agents:${listing.agent.id}:name`}>{listing.agent.name}</span>
                                    </p>
                                </div>
                            )}
                        </section>

                        <section id='description' aria-labelledby='description-title' className='border-b border-border py-8'>
                            <h2 id='description-title' className='sr-only'>About this home</h2>

                            {listing.summary && (
                                <p className='text-lg font-medium'>
                                    <span data-mdlfy-cms={`listings:${listing.id}:summary`}>{listing.summary}</span>
                                </p>
                            )}

                            {listing.description && (
                                <>
                                    <p
                                        data-mdlfy-cms={`listings:${listing.id}:description`}
                                        className={cn('mt-4 leading-relaxed whitespace-pre-line text-foreground/90', !expanded && longDescription && 'line-clamp-6')}
                                    >
                                        {listing.description}
                                    </p>

                                    {longDescription && (
                                        <button
                                            type='button'
                                            onClick={() => setExpanded(value => !value)}
                                            className='mt-3 rounded-lg text-sm font-semibold underline underline-offset-4'
                                            aria-expanded={expanded}
                                        >
                                            {expanded ? 'Show less' : 'Show more'}
                                        </button>
                                    )}
                                </>
                            )}
                        </section>

                        {listing.amenities.length > 0 && (
                            <section id='amenities' aria-labelledby='amenities-title' className='border-b border-border py-8'>
                                <h2 id='amenities-title' className='text-xl font-semibold sm:text-2xl'>What this home offers</h2>

                                <ul className='mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2'>
                                    {listing.amenities.map(amenity => {
                                        const Icon = getIcon(amenity.icon)

                                        return (
                                            <li key={amenity.id} className='flex items-center gap-4'>
                                                <Icon className='size-6 shrink-0' strokeWidth={1.5} aria-hidden='true' />
                                                <span data-mdlfy-cms={`amenities:${amenity.id}:name`}>{amenity.name}</span>
                                            </li>
                                        )
                                    })}
                                </ul>

                                {listing.categories.length > 0 && (
                                    <ul className='mt-6 flex flex-wrap gap-2' aria-label='Collections this home is part of'>
                                        {listing.categories.map(category => {
                                            const Icon = getIcon(category.icon)

                                            return (
                                                <li key={category.id}>
                                                    <Link
                                                        href={homesHref({ category: category.slug })}
                                                        className='flex h-9 items-center gap-2 rounded-full border border-border px-3.5 text-sm font-medium transition hover:border-foreground'
                                                    >
                                                        <Icon className='size-4' aria-hidden='true' />
                                                        <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                                    </Link>
                                                </li>
                                            )
                                        })}
                                    </ul>
                                )}
                            </section>
                        )}

                        <section id='payment' aria-labelledby='payment-title' className='border-b border-border py-8'>
                            <h2 id='payment-title' className='mb-5 text-xl font-semibold sm:text-2xl'>Monthly cost estimate</h2>
                            <MortgageEstimate price={listing.price} />
                        </section>

                        {listing.latitude !== null && listing.longitude !== null && (
                            <section id='location' aria-labelledby='location-title' className='border-b border-border py-8'>
                                <h2 id='location-title' className='text-xl font-semibold sm:text-2xl'>Where you'll be</h2>
                                <p className='mt-1 text-muted-foreground'>
                                    {listing.neighborhood && <span data-mdlfy-cms={`listings:${listing.id}:neighborhood`}>{listing.neighborhood}</span>}
                                    {listing.neighborhood && listing.city && ', '}
                                    {listing.city && <span data-mdlfy-cms={`listings:${listing.id}:city`}>{listing.city}</span>}
                                </p>

                                <div className='mt-5 h-80 overflow-hidden rounded-2xl sm:h-[26rem]'>
                                    <LocationMap latitude={listing.latitude} longitude={listing.longitude} label={`Location of ${listing.title}`} />
                                </div>

                                {listing.area && (
                                    <Link href={homesHref({ area: listing.area.slug })} className='mt-4 inline-block text-sm font-semibold underline underline-offset-4'>
                                        More homes in <span data-mdlfy-cms={`areas:${listing.area.id}:name`}>{listing.area.name}</span>
                                    </Link>
                                )}
                            </section>
                        )}

                        {listing.agent && (
                            <section id='agent' aria-labelledby='agent-title' className='py-8'>
                                <h2 id='agent-title' className='text-xl font-semibold sm:text-2xl'>Your agent for this home</h2>

                                <div className='mt-5 flex flex-col gap-6 rounded-2xl bg-secondary p-6 sm:flex-row'>
                                    <AgentAvatar agent={listing.agent} className='size-24' sizes='96px' />

                                    <div className='min-w-0'>
                                        <p className='text-lg font-semibold'>
                                            <span data-mdlfy-cms={`agents:${listing.agent.id}:name`}>{listing.agent.name}</span>
                                        </p>
                                        {listing.agent.role && (
                                            <p className='text-sm text-muted-foreground'>
                                                <span data-mdlfy-cms={`agents:${listing.agent.id}:role`}>{listing.agent.role}</span>
                                            </p>
                                        )}
                                        {listing.agent.bio && (
                                            <p className='mt-3 text-sm leading-relaxed'>
                                                <span data-mdlfy-cms={`agents:${listing.agent.id}:bio`}>{listing.agent.bio}</span>
                                            </p>
                                        )}

                                        <div className='mt-4 flex flex-wrap gap-3'>
                                            {listing.agent.phone && (
                                                <a
                                                    href={`tel:${listing.agent.phone.replace(/[^0-9+]/g, '')}`}
                                                    className='flex h-11 items-center gap-2 rounded-xl border border-foreground px-4 text-sm font-semibold transition hover:bg-white'
                                                >
                                                    <Phone className='size-4' aria-hidden='true' />
                                                    <span data-mdlfy-cms={`agents:${listing.agent.id}:phone`}>{listing.agent.phone}</span>
                                                </a>
                                            )}
                                            <a href='#tour' className='flex h-11 items-center rounded-xl bg-foreground px-4 text-sm font-semibold text-background'>
                                                Message the agent
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        )}
                    </div>

                    <aside aria-label='Request a tour' className='mt-8 lg:mt-0'>
                        <div className='lg:sticky lg:top-28'>
                            <TourRequestCard listing={listing} />
                        </div>
                    </aside>
                </div>

                <ListingRow id='similar' title='Similar homes' subtitle='Close in style, setting and price.' listings={similar} />
            </article>

            <div className='fixed inset-x-0 bottom-0 z-30 border-t border-border bg-white px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] lg:hidden'>
                <div className='mx-auto flex max-w-[1280px] items-center justify-between gap-4'>
                    <div>
                        <p className='font-bold'>{formatPrice(listing.price)}</p>
                        <p className='text-xs text-muted-foreground'>
                            <span>{listing.bedrooms}</span> bd · <span>{formatBaths(listing.bathrooms)}</span> ba
                        </p>
                    </div>
                    <a href='#tour' className='bg-brand-gradient flex h-12 items-center rounded-xl px-6 font-semibold text-white'>
                        Request a tour
                    </a>
                </div>
            </div>

            <PhotoTourDialog listing={listing} open={tourOpen} onOpenChange={setTourOpen} startIndex={tourStart} />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    // Homes are rendered on their first visit, so new listings from the CMS work right away.
    paths: [],
    fallback: 'blocking'
})

export const getStaticProps: GetStaticProps<ListingPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const catalog = await getCatalog()
    const listing = await getListingDetail(catalog, slug)

    if (!listing) return { notFound: true, revalidate: 60 }

    return {
        props: {
            listing,
            similar: similarHomes(listing, catalog.listings, 6),
            searchOptions: getSearchOptions(catalog)
        },
        revalidate: 60
    }
}

export default ListingPage
